import axiosClient, { axiosClientAdmin } from '@/lib/auth';
import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { Comparator, FilterOperator } from '@/lib/utils';
import {
  fetchSite,
  ProjectFullDto,
  updateSite,
  archiveSite,
  ProjectUpdateForm,
  ProjectCrudded,
  SitesSearchDto,
  getArmStatus,
  ArmStatusFetched,
  updateNoThreatDisposition,
  NoThreatSet,
  EnumArmProfile,
} from '@/app/Sites/siteApi';
import { DatalayerAssetDto, DatalayerAssetExtendedDto } from '@/types/datalayer-asset';
import { CameraFullDto } from './connectNewDevice';
import { useAuthStore } from '@/store/authStore';

export interface SiteDto {
  id: string;
  name: string;
  hasContracts: boolean;
  accountNumber: number;
  archived: boolean;
}
export interface SiteQueryResponse {
  rows: SitesSearchDto[];
  totalRows: number;
}

export interface SiteDevices {
  devices: {
    nvrId: string;
    secondaryDevice: CameraFullDto;
  }[];
}

export const useSearchSitesQuery = ({ query, isActive }: { query: string; isActive?: boolean }) =>
  useInfiniteQuery({
    queryKey: ['sites', query],
    queryFn: async ({ pageParam }) => {
      const res = await axiosClientAdmin.get<SiteQueryResponse>(
        `/customer/Contracts/search?query=${query}`,
        {
          params: {
            PageNumber: pageParam,
            PageSize: 20,
            SortBy: 'name',
            SortDir: 'Ascending',
            'Filter.Operator': 'And',
            ...((isActive ?? true)
              ? {
                'Filter.Filters[0][field]': 'archived',
                'Filter.Filters[0][action]': 'Eq',
                'Filter.Filters[0][value]': 'false',
              }
              : {}),
            'Filter.FilterGroups[0][operator]': 'Or',
            'Filter.FilterGroups[0][filters][0][field]': 'name',
            'Filter.FilterGroups[0][filters][0][value]': query,
            'Filter.FilterGroups[0][filters][0][action]': 'ILike',
          },
        }
      );
      return res.data.rows;
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length < 20) {
        return null;
      }
      return allPages.length + 1;
    },
    initialPageParam: 1,
  });

/** @deprecated In favor of functionality in api/customer/customer.ts */
export const useSearchCustomerSitesQuery = ({
  query,
  customerID,
  isActive,
  enabled = true,
}: {
  query: string;
  customerID: string;
  isActive?: boolean;
  enabled?: boolean;
}) =>
  useInfiniteQuery({
    queryKey: ['sites', query, customerID],
    enabled: enabled && Boolean(customerID),
    queryFn: async ({ pageParam }) => {
      const res = await axiosClientAdmin.get<SiteQueryResponse>(
        `/Customer/${customerID}/project/search?query=${query}`,
        {
          params: {
            PageNumber: pageParam,
            PageSize: 20,
            SortBy: 'name',
            SortDir: 'Ascending',
            'Filter.Operator': 'And',
            ...((isActive ?? true)
              ? {
                'Filter.Filters[0][field]': 'isActive',
                'Filter.Filters[0][action]': 'Eq',
                'Filter.Filters[0][value]': 'true',
              }
              : {}),
            'Filter.FilterGroups[0][operator]': 'Or',
            'Filter.FilterGroups[0][filters][0][field]': 'name',
            'Filter.FilterGroups[0][filters][0][value]': query,
            'Filter.FilterGroups[0][filters][0][action]': 'ILike',
          },
        }
      );
      return res.data.rows;
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length < 20) {
        return null;
      }
      return allPages.length + 1;
    },
    initialPageParam: 1,
  });

interface UseSiteOptions {
  siteId: string;
  companyId: string;
  enabled?: boolean;
}

export const useSite = ({ siteId, companyId, enabled = true }: UseSiteOptions) => {
  return useQuery<ProjectFullDto>({
    queryKey: ['site', siteId, companyId],
    queryFn: () => fetchSite(siteId, companyId),
    enabled: Boolean(siteId && companyId && enabled),
    staleTime: 0, // Always treat data as stale to ensure fresh data when switching sites
  });
};

export const useCustomerSitesQuery = (
  customerId: string,
  includeArchived: boolean = false,
  enabled: boolean = true
) => {
  return useQuery<SitesSearchDto[]>({
    queryKey: ['customer-sites', customerId, includeArchived],
    queryFn: async () => {
      const res = await axiosClientAdmin.get<SiteQueryResponse>(
        `/Customer/${customerId}/project/search`,
        {
          params: {
            // DISABLED DUE TO LEGACY REQUEST
            // eslint-disable-next-line custom/no-large-page-size
            PageSize: 1000,
            SortBy: 'name',
            SortDir: 'Ascending',
            // ...(!includeArchived
            //   ? {
            //       'Filter.Operator': FilterOperator.And,
            //       'Filter.Filters[0][field]': 'archivedAt',
            //       'Filter.Filters[0][action]': Comparator.Eq,
            //     }
            //   : {}),
          },
        }
      );
      return res.data.rows;
    },
    enabled: Boolean(customerId && enabled),
  });
};

interface UseUpdateSiteOptions {
  onSuccess?: (data: ProjectCrudded) => void;
  onError?: (error: Error) => void;
}

export const useUpdateSite = ({ onSuccess, onError }: UseUpdateSiteOptions = {}) => {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: ({
      customerId,
      projectId,
      data,
    }: {
      customerId: string;
      projectId: string;
      data: ProjectUpdateForm;
    }) => updateSite(customerId, projectId, data),
    onSuccess: async (data, variables) => {
      // Invalidate site query with the new query key structure
      await queryClient.invalidateQueries({
        queryKey: ['site', variables.projectId, variables.customerId],
        refetchType: 'all',
      });

      await queryClient.invalidateQueries({
        queryKey: ['sites'],
        refetchType: 'all',
      });

      await queryClient.invalidateQueries({
        queryKey: ['siteArmStatus', variables.customerId, variables.projectId],
      });

      // Invalidate customer-sites query to refresh site list in CustomerDetail component
      await queryClient.invalidateQueries({
        queryKey: ['customer-sites', variables.customerId],
        refetchType: 'all',
      });

      onSuccess?.(data);
    },
    onError: (error: Error) => {
      onError?.(error);
    },
  });

  return {
    mutateAsync,
    isPending,
  };
};

interface ArchiveSiteParams {
  companyId: string;
  siteId: string;
  site: ProjectFullDto;
  archive: boolean;
}

export const useArchiveSite = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (params: ArchiveSiteParams) =>
      archiveSite(params.companyId, params.siteId, params.site, params.archive),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['site', variables.siteId, variables.companyId] });
      queryClient.invalidateQueries({ queryKey: ['sites'] });
      queryClient.invalidateQueries({ queryKey: ['customer-sites', variables.companyId] });
      toast({
        title: 'Success',
        description: variables.archive ? 'Site archived' : 'Site restored',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update site',
        variant: 'destructive',
      });
    },
  });
};

interface SiteAssetSearchResponse {
  rows: DatalayerAssetDto[];
  totalRows: number;
}

interface SiteAssetSearchV2Response {
  rows: DatalayerAssetExtendedDto[];
  totalRows: number;
}


export const useSiteAssets = ({ siteId, companyId }: UseSiteOptions) => {
  return useQuery({
    queryKey: ['siteAssets', companyId, siteId],
    queryFn: async () => {
      try {
        const res = await axiosClientAdmin.get<SiteAssetSearchV2Response>(
          `Customer/${companyId}/project/${siteId}/assets/v2`
        );
        return res.data?.rows;
      } catch (error) {
        console.error('Error fetching site assets:', error);
        throw error;
      }
    },
    enabled: Boolean(siteId && companyId),
    retry: 1, // Limit retries on failure
  });
};

const PAGE_SIZE = 20;

export const useInfiniteSiteAssets = ({ siteId, companyId }: UseSiteOptions) => {
  return useInfiniteQuery({
    queryKey: ['siteAssets', 'infinite', companyId, siteId],
    queryFn: async ({ pageParam }) => {
      const res = await axiosClientAdmin.get<SiteAssetSearchV2Response>(
        `Customer/${companyId}/project/${siteId}/assets/v2`,
        { params: { PageNumber: pageParam, PageSize: PAGE_SIZE } }
      );
      return res.data?.rows ?? [];
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length < PAGE_SIZE) return null;
      return allPages.length + 1;
    },
    initialPageParam: 1,
    enabled: Boolean(siteId && companyId),
  });
};


interface UseUpdateArmProfileOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export const useGetArmStatus = ({ siteId, companyId, enabled = true }: UseSiteOptions) => {
  return useQuery<ArmStatusFetched>({
    queryKey: ['siteArmStatus', companyId, siteId],
    queryFn: () => getArmStatus(companyId, siteId),
    enabled: Boolean(siteId && companyId && enabled),
  });
};

export const useUpdateArmProfile = ({ onSuccess, onError }: UseUpdateArmProfileOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      companyId,
      siteId,
      site,
      armProfile,
    }: {
      companyId: string;
      siteId: string;
      site: ProjectFullDto;
      armProfile: EnumArmProfile;
    }) => {
      return updateSite(companyId, siteId, {
        ...site,
        armProfile,
      });
    },
    onSuccess: (_, { companyId, siteId }) => {
      queryClient.invalidateQueries({ queryKey: ['siteArmStatus', companyId, siteId] });
      queryClient.invalidateQueries({ queryKey: ['site', companyId, siteId] });
      onSuccess?.();
    },
    onError: (error: Error) => {
      onError?.(error);
    },
  });
};

interface UseUpdateNoThreatOptions {
  onSuccess?: (result: NoThreatSet) => void;
  onError?: (error: Error) => void;
}

export const useUpdateNoThreat = ({ onSuccess, onError }: UseUpdateNoThreatOptions = {}) => {
  return useMutation({
    mutationFn: async ({
      companyId,
      siteId,
      disarmTime,
    }: {
      companyId: string;
      siteId: string;
      disarmTime: number;
    }) => {
      return updateNoThreatDisposition(companyId, siteId, {
        disarmTime,
      });
    },
    onSuccess: (result) => {
      onSuccess?.(result);
    },
    onError: (error: Error) => {
      onError?.(error);
    },
  });
};

export interface ProjectAlertingDisarmed {
  success?: boolean;
  message?: string;
  code?: string;
  errors?: string[];
  projectFull?: ProjectFullDto;
}

interface UseDisarmSiteOptions {
  onSuccess?: (data: ProjectAlertingDisarmed) => void;
  onError?: (error: Error) => void;
}

export const useDisarmSite = ({ onSuccess, onError }: UseDisarmSiteOptions = {}) => {
  const queryClient = useQueryClient();
  const auth = useAuthStore();

  return useMutation<
    ProjectAlertingDisarmed,
    Error,
    { siteId: string; companyId: string; until?: string | null }
  >({
    mutationFn: async ({ siteId, companyId, until }) => {
      const userFullName = `${auth.user?.firstName} ${auth.user?.lastName}`;
      const response = await axiosClientAdmin.post<ProjectAlertingDisarmed>(
        `/Customer/${companyId}/project/${siteId}/disarm`,
        {
          projectId: siteId,
          until: until || null,
          disarmedBy: userFullName,
        }
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['armStatus'] });
      // Use the correct query key structure with both siteId and companyId
      queryClient.invalidateQueries({
        queryKey: ['site', variables.siteId, variables.companyId],
        refetchType: 'all',
      });

      // Also invalidate customer-sites query
      queryClient.invalidateQueries({
        queryKey: ['customer-sites', variables.companyId],
        refetchType: 'all',
      });

      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

// Hook to get all devices for a customer
export function useCustomerDevices(customerId: string) {
  return useQuery({
    queryKey: ['customerDevices', customerId],
    queryFn: async () => {
      try {
        // Fetch all sites for the customer using the correct endpoint format
        const sitesRes = await axiosClientAdmin.get<SiteQueryResponse>(
          `/Customer/${customerId}/project/search`,
          {
            params: {
              // DISABLED DUE TO LEGACY REQUEST
              // eslint-disable-next-line custom/no-large-page-size
              PageSize: 1000,
              SortBy: 'name',
              SortDir: 'Ascending',
              // 'Filter.Operator': FilterOperator.And,
              // 'Filter.Filters[0][field]': 'archivedAt',
              // 'Filter.Filters[0][action]': Comparator.Eq,
            },
          }
        );
        const sites = sitesRes.data.rows || [];

        // For each site, fetch its devices
        const devicePromises = sites.map(async (site) => {
          try {
            const res = await axiosClientAdmin.get<SiteAssetSearchResponse>(
              `/Customer/${customerId}/project/${site.id}/assets`
            );
            // Add site information to each device for grouping
            return (res.data?.rows || []).map((device) => ({
              ...device,
              siteName: site.name,
              siteId: site.id,
            }));
          } catch (error) {
            console.error(`Error fetching devices for site ${site.id}:`, error);
            return [];
          }
        });

        // Wait for all device requests to complete
        const devicesArrays = await Promise.all(devicePromises);
        // Flatten the array of arrays into a single array
        return devicesArrays.flat();
      } catch (error) {
        console.error('Error fetching customer devices:', error);
        throw error;
      }
    },
    enabled: Boolean(customerId),
    retry: 1,
  });
}
